import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "margevio_active_standort";

// Module-level shared state — every consumer reads the SAME activeStandortId
let _cachedStandorte = null;
let _pendingFetch = null;
const _listListeners = new Set();
const _activeListeners = new Set();

let _activeId = null;
try {
  _activeId = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) || null : null;
} catch { /* ignore */ }

function _notifyList() { _listListeners.forEach((cb) => cb()); }
function _notifyActive() { _activeListeners.forEach((cb) => cb(_activeId)); }

async function fetchStandorte() {
  if (_cachedStandorte) return _cachedStandorte;
  if (_pendingFetch) return _pendingFetch;
  _pendingFetch = axios
    .get(`${API}/standorte`, { withCredentials: true })
    .then((res) => {
      _cachedStandorte = Array.isArray(res.data) ? res.data : [];
      _pendingFetch = null;
      return _cachedStandorte;
    })
    .catch(() => {
      _pendingFetch = null;
      return [];
    });
  return _pendingFetch;
}

export function invalidateStandortCache() {
  _cachedStandorte = null;
  _notifyList();
}

/** Broadcasts the new activeStandortId to all useStandort() consumers. */
export function setActiveStandortGlobally(id) {
  _activeId = id || null;
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
  _notifyActive();
}

export function getActiveStandortId() {
  return _activeId;
}

export function useStandort() {
  const [standorte, setStandorte] = useState(_cachedStandorte || []);
  const [activeStandortId, setActiveStandortId] = useState(_activeId);
  const [loading, setLoading] = useState(!_cachedStandorte);

  const refresh = useCallback(async () => {
    setLoading(true);
    invalidateStandortCache();
    const data = await fetchStandorte();
    setStandorte(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchStandorte().then((data) => {
      if (!mounted) return;
      setStandorte(data);
      setLoading(false);
    });
    const listListener = () => {
      fetchStandorte().then((d) => mounted && setStandorte(d));
    };
    const activeListener = (id) => { if (mounted) setActiveStandortId(id); };
    _listListeners.add(listListener);
    _activeListeners.add(activeListener);
    return () => {
      mounted = false;
      _listListeners.delete(listListener);
      _activeListeners.delete(activeListener);
    };
  }, []);

  const setActive = useCallback((id) => setActiveStandortGlobally(id), []);

  const activeStandort = activeStandortId
    ? standorte.find((s) => s.id === activeStandortId) || null
    : null;

  return {
    standorte,
    activeStandortId,
    activeStandort,
    setActive,
    loading,
    refresh,
    /** Liefert {standort_id} fuer axios params, leer wenn 'Alle Standorte' aktiv */
    queryParam: activeStandortId ? { standort_id: activeStandortId } : {},
  };
}
